import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import type { OnInit } from '@angular/core';
import type { Observable } from 'rxjs';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    HostListener,
    computed,
    inject,
    input,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { take } from 'rxjs';

import type { CrmDeal } from '@integrations/crm-deals';
import type { CrmContact } from '@integrations/crm-contacts';
import type { CrmPipeline } from '@integrations/crm-pipelines';
import type { ChatBotEntity } from '@common/types/chat-bot.type';
import type { ESocialNetworks } from '@common/enums';
import type { WebLegacyWidgetEntity } from '@web/types';
import type { FormWidgetData } from '@web/widgets/form/type/form-widget.type';

import { EFormView, EIconType, FormStyleEnum } from '@common/enums';
import { EFormChatBots } from '@common/enums/form-chat-bots.enum';
import { EPixelTypes } from '@common/types/analytics-setings.type';
import { ERadioGroupType } from '@ui/sp-radio-group';

import type {
    AnalyticsSettingsLegacy,
    ExtendedPositionLegacy,
    FormChatBotData,
    FormWidgetElement,
    WidgetPositionLegacy,
} from '@sites/entity-core';
import type {
    FormWidgetDataModel,
    FormWidgetDefaultForm,
    FormWidgetForm,
    FormWidgetZeroForm,
} from '@sites/entity-signal-form';

import type { AddressBookVariable } from '@integrations/address-books';
import { AddressBooksUiComponent, injectAddressBooksQuery } from '@integrations/address-books';
import { injectCrmPipelinesQuery } from '@integrations/crm-pipelines';
import { injectCrmContactsQuery, isCrmDisabledError } from '@integrations/crm-contacts';
import { injectCrmDealsQuery } from '@integrations/crm-deals';
import type { CrmTag } from '@integrations/crm-tags';
import { injectCrmTagsQuery } from '@integrations/crm-tags';
import type { AddressBookTag } from '@integrations/address-books-tags';
import { injectAddressBooksTagsQuery } from '@integrations/address-books-tags';

import { AppFacade } from '@web/store/app/app.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { ChatBotsCollectionFacade } from '@web/store/collections/chat-bots/chat-bots.facade';

import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';
import { getSocialNetworkIconSrc } from '@web/shared/components/web-social-networks/utils';

import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { SpVariablesPopoverComponent } from '@ui/sp-variables-popover';
import { WebAnalyticsSettingsSignalLegacyComponent } from '@web/shared/components/web-analytics-settings-signal-legacy/web-analytics-settings-signal-legacy.component';
import {
    WebCrmPipelinesCollectionComponent,
    type SetPipelineEventPayload,
} from '@web/shared/components/web-crm-pipelines-collection';
import { WebTagsCollectionComponent } from '@web/shared/components/web-tags-collection/web-tags-collection.component';
import { WebWidgetExtendedHideSettingsComponent } from '@web/shared/components/web-widget-extended-hide-settings';
import { WebWidgetHideSettingsComponent } from '@web/shared/components/web-widget-hide-settings';
import { WebWidgetPositionSettingsComponent } from '@web/shared/components/web-widget-position-settings';
import { SpExtendedPositionSignalSettingsComponent } from '@components/sp-extended-position-signal-settings';

import { FormAdditionalSettingsComponent } from './form-additional-settings/form-additional-settings.component';
import { FormButtonSettingsComponent } from './form-button-settings/form-button-settings.component';
import { FormChatBotButtonSettingsComponent } from './form-chat-bot-button-settings/form-chat-bot-button-settings.component';
import { FormChatBotSettingsComponent } from './form-chat-bot-settings/form-chat-bot-settings.component';
import { FormElementSettingsComponent } from './form-element-settings/form-element-settings.component';
import { FormInputSettingsComponent } from './form-input-settings/form-input-settings.component';

enum CurrentSettings {
    MAIN = 'main',
    BUTTON = 'button',
    FORM_BUTTON = 'form-button',
    ELEMENT = 'element',
    INPUT = 'input',
    ADDITIONAL = 'additional',
    ANALYTICS = 'analytics',
    CHAT_BOT_SETTINGS = 'chat-bot-settings',
    CHAT_BOT_BUTTON_STYLES = 'chat-bot-button-styles',
}

/** Mirrors `Validators.required` semantics for plain values (0 is non-empty). */
/**
 * Signal-form rewrite of the subscription-form control panel. The widget data
 * lives in the shared `FormWidgetForm` (see `WebLegacyWidgetSignalFormService`);
 * every sub-panel edits that form live inside a transaction frame it opens and
 * closes itself (`FormSettingsPanel`), so save commits one history entry
 * and cancel/escape/closing the panel rolls the edits back. The CRM
 * pipelines / tags / address-books collection components all speak the signal
 * value-in/event-out API — no bridge controls remain.
 */
@Component({
    selector: 'web-form-control',
    templateUrl: './form-control.component.html',
    styleUrls: ['./form-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AsyncPipe,
        DragDropModule,
        BsDropdownModule,
        TooltipModule,
        CommonElementsModule,
        SpVariablesPopoverComponent,
        AddressBooksUiComponent,
        WebCrmPipelinesCollectionComponent,
        WebTagsCollectionComponent,
        WebWidgetPositionSettingsComponent,
        WebWidgetHideSettingsComponent,
        WebWidgetExtendedHideSettingsComponent,
        SpExtendedPositionSignalSettingsComponent,
        WebAnalyticsSettingsSignalLegacyComponent,
        FormButtonSettingsComponent,
        FormElementSettingsComponent,
        FormAdditionalSettingsComponent,
        FormInputSettingsComponent,
        FormChatBotButtonSettingsComponent,
        FormChatBotSettingsComponent,
    ],
})
export class FormControlComponent implements OnInit {
    public readonly widget = input.required<WebLegacyWidgetEntity<FormWidgetData>>();
    public readonly isSpecific = input<boolean>(false);
    public readonly isEduSite = input<boolean>(false);

    private readonly translateService = inject(TranslateService);
    private readonly signalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly appFacade = inject(AppFacade);
    private readonly layoutFacade = inject(LayoutFacade);
    private readonly chatBotsCollectionFacade = inject(ChatBotsCollectionFacade);
    private readonly destroyRef = inject(DestroyRef);

    private readonly EMPTY_LINK_TEXT = this.translateService.instant('provide_link');

    // --- Collections (unchanged from the FormGroup era) ---

    private readonly isAdmin = toSignal(this.appFacade.isAdmin$, { initialValue: false });

    protected readonly crmPipelinesQuery = injectCrmPipelinesQuery(() => ({ enabled: !this.isAdmin() }));
    public readonly crmPipelines = computed<CrmPipeline[]>(() =>
        this.isAdmin() ? [] : (this.crmPipelinesQuery.data() ?? [])
    );

    protected readonly crmContactsQuery = injectCrmContactsQuery(() => ({ enabled: !this.isAdmin() }));
    public readonly crmContacts = computed<CrmContact[]>(() =>
        this.isAdmin() ? [] : (this.crmContactsQuery.data() ?? [])
    );
    /**
     * Only a definitive rejection of contacts/attributes means CRM is disabled for the
     * account — a transient network failure leaves the CRM settings in place (#71534).
     * Admin/template mode has no real account (the query is disabled), so it never
     * counts as CRM-disabled regardless of the query state.
     */
    public readonly isCrmDisabled = computed(
        () => !this.isAdmin() && isCrmDisabledError(this.crmContactsQuery.error())
    );

    protected readonly crmTagsQuery = injectCrmTagsQuery(() => ({ enabled: !this.isAdmin() }));
    public readonly crmTags = computed<CrmTag[]>(() => (this.isAdmin() ? [] : (this.crmTagsQuery.data() ?? [])));

    protected readonly addressBooksTagsQuery = injectAddressBooksTagsQuery(() => ({ enabled: !this.isAdmin() }));
    public readonly addressBooksTags = computed<AddressBookTag[]>(() =>
        this.isAdmin() ? [] : (this.addressBooksTagsQuery.data() ?? [])
    );

    /** The deals query key follows the pipeline selected in the widget data. */
    private readonly selectedPipelineId = computed<number | null>(() => {
        const pipeline = this.form.selectedPipeline().value() as CrmPipeline | null;
        return pipeline?.id ?? null;
    });
    protected readonly crmDealsQuery = injectCrmDealsQuery(
        () => this.selectedPipelineId(),
        () => ({ enabled: !this.isAdmin() })
    );
    public readonly crmDeals = computed<CrmDeal[]>(() => (this.isAdmin() ? [] : (this.crmDealsQuery.data() ?? [])));

    public chatBots$(messenger: string): Observable<ChatBotEntity[]> {
        return this.appFacade.isAdminCollectionWrapper$(this.chatBotsCollectionFacade.chatBots$(messenger));
    }

    // --- Signal form ---

    // Created in `ngOnInit` (it needs the `widget` input). Every computed/query/effect below reads it lazily,
    // and none of them is evaluated before the first change detection, which runs after `ngOnInit`.
    public form!: FormWidgetForm;

    private readonly isAddressBookEnabled = computed(() => Boolean(this.form.createAddressBook().value()));

    protected readonly addressBooksQuery = injectAddressBooksQuery(() => ({ enabled: this.isAddressBookEnabled() }));

    public addressBookVariablesById(bookId: number | null): AddressBookVariable[] {
        return this.addressBooksQuery.data()?.find((b) => b.id === bookId)?.variables ?? [];
    }

    // --- Panel state ---

    public readonly settingEnum = CurrentSettings;
    public readonly currentSettings = signal<CurrentSettings>(CurrentSettings.MAIN);
    public readonly editedInputIndex = signal<number | null>(null);
    public readonly editingChatBotIndex = signal<number | null>(null);

    public readonly editingChatBot = computed<FormChatBotData | null>(() => {
        const index = this.editingChatBotIndex();
        if (index === null) return null;
        return this.form.chatBots().value()[index] ?? null;
    });

    public readonly existingChatBots = signal<Partial<Record<string, string>>>({
        telegram: 'telegram',
        instagram: 'instagram',
        viber: 'viber',
        messenger: 'messenger',
        whatsapp: 'whatsapp',
        //tiktok: 'tiktok',
    });

    public readonly isDeviceDesktop = toSignal(this.layoutFacade.isDeviceDesktop$, { initialValue: true });
    public readonly isDeviceTablet = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });
    public readonly isDeviceMobile = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });

    public readonly radioGroupType = ERadioGroupType;
    public readonly FormStyleEnum = FormStyleEnum;
    protected readonly pixelTypes = EPixelTypes;
    protected readonly EFormView = EFormView;

    public readonly CREATE_DEAL_AND_CONTACT = [
        { label: this.translateService.instant('create_deal_and_contact'), value: true },
        { label: this.translateService.instant('create_only_contact'), value: false },
    ];

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<FormWidgetForm>(this.widget().id, this.destroyRef);

        Object.keys(EFormChatBots).forEach((chatBotName) => {
            this.appFacade
                .isAdminCollectionWrapper$(this.chatBotsCollectionFacade.chatBots$(chatBotName))
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((chatBots) => {
                    this.existingChatBots.update((current) => {
                        const next = { ...current };
                        if (!chatBots || chatBots.length === 0) {
                            delete next[chatBotName];
                        } else {
                            next[chatBotName] = chatBotName;
                        }
                        return next;
                    });
                });
        });
    }

    // --- Panel switching (each sub-panel opens and closes its own transaction) ---

    public openSettings(panel: CurrentSettings): void {
        this.currentSettings.set(panel);
    }

    public closeSettings(): void {
        this.currentSettings.set(CurrentSettings.MAIN);
        this.editedInputIndex.set(null);
        this.editingChatBotIndex.set(null);
    }

    public setPipeline({ pipeline, step, settings, skipHistory }: SetPipelineEventPayload): void {
        const apply = () => {
            this.form.setSelectedPipeline(pipeline as unknown as FormWidgetDataModel['selectedPipeline']);
            this.form.setSelectedPipelineStep(step as unknown as FormWidgetDataModel['selectedPipelineStep']);
            this.form.setSelectedPipelineSettings(
                settings as unknown as FormWidgetDataModel['selectedPipelineSettings']
            );
            this.clearCrmVariables();
        };

        // The pipelines collection auto-picks a default on load — that write
        // must not land in undo history.
        if (skipHistory) {
            this.form.transactWithoutHistory(apply);
            return;
        }

        apply();
    }

    /**
     * Silent rename sync when the pipeline was renamed in CRM — the FormGroup
     * original patched with `emitEvent: false`, i.e. no history entry.
     */
    public setPipelineName(name: string): void {
        const selectedPipeline = this.form.selectedPipeline().value();
        if (!selectedPipeline) return;

        this.form.transactWithoutHistory(() => {
            this.form.setSelectedPipeline({
                ...selectedPipeline,
                name,
            } as unknown as FormWidgetDataModel['selectedPipeline']);
        });
    }

    public setCRMCreationType(value: boolean): void {
        this.form.transact(() => {
            if (!value) {
                this.setPipeline({ pipeline: null, step: null, settings: null });
                this.form.setDealName(null);
            }

            this.form.setCreateContactAndDeal(value);
        });
    }

    public getCRMCreationType(): string {
        const createContactAndDeal = this.form.createContactAndDeal().value();
        return this.CREATE_DEAL_AND_CONTACT.find((el) => el.value === createContactAndDeal)?.label;
    }

    public onDealNameInput(event: Event): void {
        this.form.setDealName((event.target as HTMLInputElement).value);
    }

    private withClearedCrmVariables(elements: FormWidgetElement[]): FormWidgetElement[] {
        return (elements ?? []).map((el) => ({ ...el, crmContactVariable: null, crmDealVariable: null }));
    }

    private clearCrmVariables(): void {
        this.form.setElements(this.withClearedCrmVariables(this.form.elements().value()));
    }

    private clearAddressBookVariables(): void {
        const elements = this.form.elements().value();

        this.form.setElements(
            elements.map((el) => ({
                ...el,
                variable: el.type === 'email' || el.type === 'phone' ? el.variable : null,
            }))
        );
    }

    public onSelectedAddressBookChange(bookId: number): void {
        this.form.transact(() => {
            this.form.setSelectedAddressBook(bookId);
            this.clearAddressBookVariables();
        });
    }

    public onContactTagsChange(tagIds: number[]): void {
        this.form.setContactTags(tagIds);
    }

    public onContactAddressBookTagsChange(tagIds: number[]): void {
        this.form.setContactAddressBookTags(tagIds);
    }

    // --- Toggles ---

    public toggleAddressBookSettings(val: boolean): void {
        this.form.transact(() => {
            if (!val) {
                this.clearAddressBookVariables();
            }

            this.form.setSelectedAddressBook(null);
            this.form.setCreateAddressBook(val);
        });
    }

    public toggleAddCrmTags(val: boolean): void {
        this.form.transact(() => {
            if (!val) {
                this.form.setContactTags([]);
            }

            this.form.setAddCrmTags(val);
        });
    }

    public toggleAddAddressBookTags(val: boolean): void {
        this.form.transact(() => {
            if (!val) {
                this.form.setContactAddressBookTags([]);
            }

            this.form.setAddAddressBookTags(val);
        });
    }

    public toggleTwoStepForm(val: boolean): void {
        this.form.transact(() => {
            this.form.setIsTwoStepForm(val);
            this.moveEmailElementFirst();

            // Enabling a two-step form implies re-submitting the contact after
            // the second step, so the CRM contact must be overwritten with the
            // combined data — force `updateExistingContact` on.
            if (val) {
                this.form.setAdditionalFormSettings({
                    ...this.form.additionalFormSettings().value(),
                    updateExistingContact: true,
                });
            }
        });
    }

    public setFormView(value: EFormView): void {
        this.form.setFormView(value as unknown as FormWidgetDataModel['formView']);

        if (value === EFormView.static && this.form.isTwoStepForm().value()) {
            this.form.setIsTwoStepForm(false);
        }
    }

    public onModalTitleInput(event: Event): void {
        this.form.setModalTitle((event.target as HTMLInputElement).value);
    }

    public setInputSettingsHorizontalPosition(val: string): void {
        this.form.setInputSettings({
            ...this.form.inputSettings().value(),
            horizontalPosition: val as FormWidgetDataModel['inputSettings']['horizontalPosition'],
        });
    }

    // --- Elements ---

    public editInput(i: number): void {
        this.editedInputIndex.set(i);
        this.openSettings(CurrentSettings.ELEMENT);
    }

    public removeInput(i: number): void {
        this.form.removeElement(i);
    }

    public addInput(): void {
        this.form.addElement({
            type: 'input',
            label: this.translateService.instant('control_entry_field'),
            variable: null,
            crmContactVariable: null,
            crmDealVariable: null,
            telegramVariable: null,
            instagramVariable: null,
            whatsappVariable: null,
            tiktokVariable: null,
            viberVariable: null,
            messengerVariable: null,
            placeholder: this.translateService.instant('control_input_placeholder'),
            required: false,
            protected: false,
            isDynamicPlaceholderEnabled: false,
            htmlInputType: 'text',
            autocompleteType: 'off',
        });
    }

    public dropElementItem(event: CdkDragDrop<FormWidgetElement[]>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        this.form.moveElement(event.previousIndex, event.currentIndex);
    }

    public moveEmailElementFirst(): void {
        const elements = this.form.elements().value();
        const emailIndex = elements.findIndex((el) => el.type === 'email');

        if (emailIndex > 0) {
            this.form.moveElement(emailIndex, 0);
        }
    }

    public isDisabledDelete(element: FormWidgetElement): boolean {
        if (this.form.addChatBotToForm().value()) return false;

        const elements = this.form.elements().value();
        const hasEmail = elements.some((el) => el.type === 'email');
        const hasPhone = elements.some((el) => el.type === 'phone');

        return (Boolean(element.protected) && !hasEmail) || (Boolean(element.protected) && !hasPhone);
    }

    public getControlHandleIcon(type: string): string {
        return `./assets/img/websites/forms-icons/lpc-f-${type}-icon.svg`;
    }

    // --- Chat bots ---

    public toggleChatBotsSettings(val: boolean): void {
        this.form.transact(() => {
            if (val) {
                const key = Object.keys(this.existingChatBots())[0];
                this.form.setChatBots([
                    {
                        socialNetwork: key as FormChatBotData['socialNetwork'],
                        type: 'icon',
                        iconType: 'round',
                        name: key.charAt(0).toUpperCase() + key.slice(1),
                        link: '',
                        description: '',
                        botName: '',
                        botId: '',
                        variables: {
                            bot_id: null,
                            _id: null,
                            name: null,
                            value_type: null,
                            value: null,
                            created_at: null,
                            updated_at: null,
                        },
                        botChain: {
                            chainId: null,
                            name: '',
                            isDefault: false,
                        },
                        uri: null,
                        phone: null,
                        preventLinkConverting: null,
                    },
                ]);
            } else {
                this.form.setChatBots([]);

                const elements = this.form.elements().value();
                const hasEmail = elements.some((field) => field.type === 'email');
                if (!hasEmail) {
                    this.form.addElement({
                        type: 'email',
                        label: 'Email',
                        placeholder: 'username@gmail.com',
                        required: true,
                        protected: true,
                        isDynamicPlaceholderEnabled: false,
                        crmContactVariable: null,
                        crmDealVariable: null,
                        variable: 'email',
                        htmlInputType: 'email',
                        autocompleteType: 'email',
                    });
                }
            }

            this.form.setAddChatBotToForm(val);
        });
    }

    public editChatBot(index: number): void {
        const chatBot = this.form.chatBots().value()[index];
        if (!chatBot) return;

        this.appFacade
            .isAdminCollectionWrapper$(this.chatBotsCollectionFacade.chatBots$(chatBot.socialNetwork))
            .pipe(take(1))
            .subscribe(() => {
                this.editingChatBotIndex.set(index);
                this.openSettings(CurrentSettings.CHAT_BOT_SETTINGS);
            });
    }

    public onEditingChatBotChange(value: FormChatBotData): void {
        const index = this.editingChatBotIndex();
        if (index === null) return;

        this.form.updateChatBot(index, value);
    }

    public removeSocial(i: number): void {
        if (this.form.chatBots().value().length === 1) {
            return;
        }

        this.form.removeChatBot(i);
    }

    public addChatBot(): void {
        const socialNetwork = this.getUnselectedSocialNetwork();

        this.form.addChatBot({
            socialNetwork: socialNetwork as unknown as FormChatBotData['socialNetwork'],
            type: this.form.chatBotsButtonSettings().value().type ?? 'icon',
            iconType: 'outlined',
            name: socialNetwork,
            link: null,
            description: null,
            botName: null,
            botId: null,
            variables: {
                bot_id: null,
                _id: null,
                name: null,
                value_type: null,
                value: null,
                created_at: null,
                updated_at: null,
            },
            botChain: {
                chainId: null,
                name: null,
                isDefault: null,
            },
            uri: null,
            phone: null,
            preventLinkConverting: null,
        });
    }

    private getUnselectedSocialNetwork(): EFormChatBots {
        const chatBots = this.form.chatBots().value();
        const selected = chatBots.map((el) => el.socialNetwork);
        const difference = this.getMessengerNames().filter(
            (type) => !selected.includes(type as unknown as FormChatBotData['socialNetwork'])
        );

        return difference.length > 0 ? difference[0] : this.getMessengerNames()[0];
    }

    public getMessengerNames(): EFormChatBots[] {
        return Object.keys(this.existingChatBots()) as EFormChatBots[];
    }

    public checkExistingChatBotsNumber(): number {
        return Object.keys(this.existingChatBots()).length;
    }

    public dropChatBotElementItem(event: CdkDragDrop<FormChatBotData[]>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        this.form.moveChatBot(event.previousIndex, event.currentIndex);
    }

    public getSocialNetworkIconSrcByChatBot(chatBot: FormChatBotData): string {
        return getSocialNetworkIconSrc(EIconType.logos, chatBot.socialNetwork as unknown as ESocialNetworks);
    }

    public getChatBotSubText(chatBot: FormChatBotData): string {
        return chatBot.link ? chatBot.link : this.EMPTY_LINK_TEXT;
    }

    // --- Analytics (no transaction — the original applied edits live) ---

    public onAnalyticsSettingsChange(value: AnalyticsSettingsLegacy): void {
        this.form.setAnalyticsSettings(value);
    }

    // --- Layout / misc ---

    /** True when the widget lives inside a zero block — drives template branching. */
    public get isZero(): boolean {
        return this.widget().type === 'zero';
    }

    /** The form narrowed to the default variant — see the social control for the casting rationale. */
    public get defaultForm(): FormWidgetDefaultForm {
        return this.form as FormWidgetDefaultForm;
    }

    /** The form narrowed to the zero variant. */
    public get zeroForm(): FormWidgetZeroForm {
        return this.form as FormWidgetZeroForm;
    }

    /**
     * Snapshot for the value/event-based `sp-extended-position-signal-settings`
     * component — `computed()` so the binding re-evaluates when the form's
     * position branch changes. The position accessor is union-typed (simple
     * grid on default placement), hence the cast.
     */
    public readonly zeroPosition = computed<ExtendedPositionLegacy | null>(() =>
        this.isZero ? (this.form.position().value() as ExtendedPositionLegacy) : null
    );

    public onZeroPositionChange(position: ExtendedPositionLegacy): void {
        this.zeroForm.setPosition(position);
    }

    public onPositionChange(patch: Partial<WidgetPositionLegacy>): void {
        const current = this.defaultForm.position().value();
        this.defaultForm.setPosition({ ...current, ...patch });
    }
}
